/**
 * A scroll the page starts on purpose — an anchor jump, a hash deep link — is
 * indistinguishable from a user scroll for the header's direction logic. Left
 * alone, a downward jump trips the hide threshold and pushes the very anchor
 * navigation off-screen that the reader just used, and the space reserved by
 * `scroll-margin-top` ends up empty (#1778).
 *
 * Whoever scrolls deliberately announces it here; the header then leaves its
 * direction state alone until the scroll has come to rest.
 */

/** Frames without movement before the scroll counts as finished. */
const SETTLE_FRAMES = 3;

/**
 * Frames to wait before a standstill may end the lock at all. A smooth scroll
 * only starts on the next frame, so the untouched position of the first frames
 * must not be read as a finished scroll.
 */
const MIN_FRAMES = 8;

/**
 * Last resort. `scrollend` is not reliably available (notably on iOS Safari,
 * which is where #1778 was reported), so the lock must never depend on the
 * scroll actually reaching a standstill.
 */
const HARD_LIMIT = 1500;

/** A genuine user gesture hands control back immediately. */
const USER_GESTURES = ['touchstart', 'wheel', 'keydown'] as const;

let holding = false;
let release: (() => void) | null = null;

/** Whether a deliberate scroll is currently in flight. */
export function isProgrammaticScroll(): boolean {
  return holding;
}

/**
 * Announce a deliberate scroll and hold the lock until it settles, a user
 * gesture takes over, or the hard limit expires.
 */
export function beginProgrammaticScroll(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // A second jump interrupting the first restarts the watch instead of
  // stacking a second one — otherwise the first timer would release the lock
  // in the middle of the second jump.
  release?.();
  holding = true;

  let frame = 0;
  let limit = 0;
  let frames = 0;
  let standstill = 0;
  let lastPosition = window.scrollY;

  const stop = () => {
    holding = false;
    release = null;
    window.cancelAnimationFrame(frame);
    window.clearTimeout(limit);
    USER_GESTURES.forEach((type) => window.removeEventListener(type, stop));
  };

  const watch = () => {
    const position = window.scrollY;
    standstill = position === lastPosition ? standstill + 1 : 0;
    lastPosition = position;
    frames += 1;

    if (frames >= MIN_FRAMES && standstill >= SETTLE_FRAMES) {
      stop();
      return;
    }

    frame = window.requestAnimationFrame(watch);
  };

  limit = window.setTimeout(stop, HARD_LIMIT) as unknown as number;
  release = stop;
  USER_GESTURES.forEach((type) =>
    window.addEventListener(type, stop, { passive: true }),
  );
  frame = window.requestAnimationFrame(watch);
}
